import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSiteAuditLog, type SiteAuditEntry } from '../../services/adminApi';

interface Props {
  siteId: string;
}

export function SiteSettingsAuditLog({ siteId }: Props) {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['site-audit-log', siteId, page],
    queryFn: () => fetchSiteAuditLog(siteId, { page, limit: 10 }),
    enabled: !!siteId,
  });

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-4">
      <h2 className="text-sm font-semibold text-ems-text-primary mb-3">Change history</h2>
      {isLoading && <div className="text-ems-text-muted text-sm">Loading...</div>}
      {isError && <div className="text-ems-red text-sm">Failed to load audit log.</div>}
      {data && data.entries.length === 0 && (
        <div className="text-ems-text-muted text-sm">No changes yet.</div>
      )}
      <ul className="space-y-2">
        {data?.entries.map((entry) => (
          <li key={entry.log_id} className="text-sm border border-ems-border rounded-lg p-3">
            <div className="flex justify-between gap-2">
              <span className="text-ems-text-primary truncate">{entry.actor_email}</span>
              <span className="text-ems-text-muted text-xs whitespace-nowrap">
                {new Date(entry.created_at).toLocaleString()}
              </span>
            </div>
            <div className="text-ems-text-muted text-xs mt-1">
              Changed: {entry.details.changed_fields.join(', ')}
            </div>
            <button
              type="button"
              onClick={() =>
                setExpanded(expanded === entry.log_id ? null : entry.log_id)
              }
              className="text-ems-green text-xs mt-1 hover:underline"
            >
              {expanded === entry.log_id ? 'Hide diff' : 'Show diff'}
            </button>
            {expanded === entry.log_id && <DiffView entry={entry} />}
          </li>
        ))}
      </ul>
      {data && data.pagination.total_pages > 1 && (
        <div className="flex justify-between items-center mt-3 text-xs text-ems-text-muted">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="disabled:opacity-50 hover:text-ems-green"
          >
            Prev
          </button>
          <span>
            Page {page} of {data.pagination.total_pages}
          </span>
          <button
            type="button"
            disabled={page >= data.pagination.total_pages}
            onClick={() => setPage((p) => p + 1)}
            className="disabled:opacity-50 hover:text-ems-green"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function DiffView({ entry }: { entry: SiteAuditEntry }) {
  return (
    <div className="mt-2 space-y-1 text-xs">
      {entry.details.changed_fields.map((f) => (
        <div key={f} className="grid grid-cols-[auto_1fr_1fr] gap-2">
          <span className="font-mono text-ems-text-muted">{f}:</span>
          <span className="text-ems-red/80 line-through break-all">
            {JSON.stringify(entry.details.old_values[f])}
          </span>
          <span className="text-ems-green break-all">
            {JSON.stringify(entry.details.new_values[f])}
          </span>
        </div>
      ))}
    </div>
  );
}
